import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Card } from 'core/components/card';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { NotificationCard } from 'core/components/notification-card';
import { RenameDocumentModal } from 'core/components/rename-document-modal';
import { ConfirmDeleteInternalDocumentModal } from 'customer/components/documents/components/confirm-delete-internal-document-modal';
import { DocumentImagePreviewModal, isRenderableImage } from 'common/components/document-image-preview-modal';
import darkInfoIcon from 'core/assets/svg/ic-info-black.svg';
import reviewImagesIcon from 'core/assets/svg/ic-review-images.svg';

import { isDeleteSupportingDocumentsAllowed } from 'core/helpers/delete-supporting-documents';
import OfferDocumentUpload from './offer-document-upload';
import OfferDocumentList from './offer-document-list';
import useStyles from './offer-document-modal.styles';

const OfferDocumentModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { viewOnly, canDeleteInternalDocuments } = useSession();
  const [documentToRename, setDocumentToRename] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    offer: {
      documents,
      documentsLoading,
      webUserId,
      uploadDocument,
      downloadDocument,
      getDocumentSignedUrl,
      fetchDocuments,
      renameDocument,
      deleteDocument
    }
  } = useStore();

  const hasPreviewableImages = documents.some((document) => isRenderableImage(document.id));

  // Fetch documents when modal opens
  useEffect(() => {
    if (open && webUserId) {
      fetchDocuments().catch((error) => {
        console.error(error);
        toast.notify({
          type: 'error',
          message: 'Failed to load documents'
        });
      });
    }
  }, [open, webUserId, fetchDocuments, toast]);

  const handleUpload = async (file, onProgress) => {
    try {
      const uploadedDocument = await uploadDocument(file, onProgress);
      toast.notify({
        type: 'success',
        message: 'Document uploaded successfully'
      });
      return uploadedDocument;
    } catch (error) {
      toast.notify({
        type: 'error',
        message: 'Failed to upload document'
      });
      throw error;
    }
  };

  const handleDownload = async (path) => {
    try {
      await downloadDocument(path);
    } catch (error) {
      toast.notify({
        type: 'error',
        message: 'Failed to download document'
      });
      throw error;
    }
  };

  // Rename a document by S3 path (shared by the table's rename modal and the image
  // gallery). Rethrows so the rename modal stays open on failure. `renameDocument`
  // re-fetches the list, so both the table and gallery reflect the new name.
  const renameWithToast = async (path, label) => {
    try {
      await renameDocument(path, label);
      toast.notify({
        type: 'success',
        message: 'Document renamed successfully'
      });
    } catch (error) {
      toast.notify({
        type: 'error',
        message: 'Failed to rename document'
      });
      throw error;
    }
  };

  // Delete a supporting document. `deleteDocument` re-fetches the list on success, so the
  // table reflects the removal. On failure we surface a toast and leave `documentToDelete`
  // set, which keeps the confirm modal open — the error is intentionally swallowed here
  // because the confirm modal's onConfirm has no catch of its own.
  const handleDelete = async () => {
    try {
      await deleteDocument(documentToDelete.id);
      toast.notify({
        type: 'success',
        message: 'Document deleted successfully'
      });
      setDocumentToDelete(null);
    } catch {
      toast.notify({
        type: 'error',
        message: 'Failed to delete document'
      });
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="md" classes={{ paper: classes.container }}>
      <Label type="greenBig" className={classes.modalHeader}>
        Supporting Documents
      </Label>
      <Label type="darkGreenSmall" className={classes.modalContent}>
        Upload any requested documents here to confirm applicants or insured items meet conditions related to discounts,
        rejections, or previous coverage.
      </Label>

      <NotificationCard iconSize="icon16" icon={darkInfoIcon} type="light">
        <span>
          Got any task documents? You can upload them in the <b>Post-purchase tasks</b> section at Checkout.
        </span>
      </NotificationCard>

      <div className={classes.content}>
        <Card className={classes.documentsCard}>
          <OfferDocumentList
            documents={documents}
            onDownload={handleDownload}
            onRename={setDocumentToRename}
            onDelete={setDocumentToDelete}
            canRename={!viewOnly}
            canDelete={canDeleteInternalDocuments && isDeleteSupportingDocumentsAllowed()}
            loading={documentsLoading}
          />
          {!viewOnly && <OfferDocumentUpload onUpload={handleUpload} />}
        </Card>
      </div>

      <div className={classes.footer}>
        {hasPreviewableImages ? (
          <Button
            disableFocusRipple
            className={classes.previewImagesButton}
            onClick={() => setPreviewOpen(true)}
            variant="text"
            color="secondary"
          >
            Preview all images
            <img alt="review images icon" className={classes.previewImagesIcon} src={reviewImagesIcon} />
          </Button>
        ) : null}
        <Button variant="contained" color="primary" onClick={onClose}>
          Done
        </Button>
      </div>
      {documentToRename ? (
        <RenameDocumentModal
          fileName={documentToRename.fileName}
          currentLabel={documentToRename.displayName || ''}
          onSave={(label) => renameWithToast(documentToRename.id, label)}
          onClose={() => setDocumentToRename(null)}
        />
      ) : null}
      {documentToDelete ? (
        <ConfirmDeleteInternalDocumentModal
          fileName={documentToDelete.displayName || documentToDelete.fileName}
          onConfirm={handleDelete}
          onClose={() => setDocumentToDelete(null)}
        />
      ) : null}
      {previewOpen ? (
        <DocumentImagePreviewModal
          open
          onClose={() => setPreviewOpen(false)}
          documents={documents.map((document) => ({
            path: document.id,
            lastModified: document.uploadedAt,
            displayName: document.displayName
          }))}
          getDocName={(path) => path.split('/').pop()}
          getSignedUrl={getDocumentSignedUrl}
          onRename={renameWithToast}
          canRenameDocument={(document) => !viewOnly && document.path.startsWith('offer/')}
          title="Supporting document images"
        />
      ) : null}
    </BaseDialog>
  );
};

OfferDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(OfferDocumentModal);
